import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";

export function buildRadarOption({ query, data, palette, base, xKey }: ChartBuildCtx): ChartOption {
  // Use min/max range per axis so closely-clustered values still spread
  // visually. A radar of "open_rate, reply_rate, …" across recruiters
  // where every recruiter is 9-19% will look like overlapping dots
  // when axis 0 is forced — pin to the actual data range with a 10%
  // pad so the cheapest signal still differentiates.
  const indicators = query.measures.map((m) => {
    const values = data.rows.map((r) => Number(r[m] ?? 0));
    // Empty / all-NaN values → Math.min/max would return ±Infinity
    // which ECharts treats as a runtime error. Fall back to a 0-1
    // range so the empty-state polygon still renders cleanly.
    const finite = values.filter((v) => Number.isFinite(v));
    const lo = finite.length > 0 ? Math.min(...finite) : 0;
    const hi = finite.length > 0 ? Math.max(...finite) : 1;
    const span = hi - lo || 1;
    return {
      name: humanizeMeasure(m),
      min: Math.max(0, lo - span * 0.1),
      max: hi + span * 0.1,
    };
  });
  // Cap at 5 series — past that, the polygons overlap into a single
  // illegible blob even with low-opacity fill. Sort by total of all
  // measures so the leaders are kept.
  const capped = data.rows
    .map((r, i) => ({
      row: r,
      total: query.measures.reduce((acc, m) => acc + Number(r[m] ?? 0), 0),
      i,
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);
  const dashPatterns: Array<number[] | undefined> = [
    undefined,        // 0: solid
    undefined,        // 1: solid
    [6, 4],           // 2: long dash
    [2, 3],           // 3: dotted
    [10, 4, 2, 4],    // 4: dash-dot
  ];
  const seriesData = capped.map((entry, idx) => ({
    // Prefer the row's category value; for a single overall series with no
    // grouping dimension (row has no xKey value), label it "Overall" rather
    // than the meaningless "series-0".
    name: String(entry.row[xKey] ?? (capped.length === 1 ? "Overall" : `Series ${entry.i + 1}`)),
    value: query.measures.map((m) => Number(entry.row[m] ?? 0)),
    // Disable area fill past the second series — overlapping fills
    // muddy the chart. The line + dash pattern carries the identity.
    areaStyle: idx < 2 ? { opacity: 0.18 } : undefined,
    lineStyle: { width: 2, type: dashPatterns[idx] ?? undefined },
    symbol: "circle",
    symbolSize: 5,
    itemStyle: { color: palette[idx % palette.length], borderColor: "#fff", borderWidth: 1 },
  }));
  return {
    ...base,
    tooltip: {
      trigger: "item",
      formatter: (p: { name: string; value: number[] }) => {
        const lines = query.measures.map((m, i) => {
          const fmt = formatFromColumn(m, data.columns);
          return `${humanizeMeasure(m)}: <b>${formatValue(p.value[i], fmt)}</b>`;
        });
        return `<b>${p.name}</b><br/>${lines.join("<br/>")}`;
      },
    },
    legend: {
      show: true,
      bottom: 4,
      // Wrap the legend across multiple lines instead of letting the
      // last few series get clipped off the right edge.
      type: "scroll",
      textStyle: { color: "#6b7280", fontSize: 11 },
      formatter: (name: string) => (name.length > 18 ? name.slice(0, 17) + "…" : name),
    },
    radar: {
      indicator: indicators,
      shape: "polygon",
      splitNumber: 4,
      // ECharts' auto-sized polygon can hug the canvas edges and clip
      // axis name labels (esp. the bottom "south" axis). A 58% radius
      // leaves a margin on all four sides for axisName text + room at
      // the very bottom for the legend.
      radius: "58%",
      center: ["50%", "46%"],
      axisName: {
        color: "#6b7280",
        fontSize: 11,
        // Truncate long measure names so they don't bleed past the
        // chart edge on the east / west axes; the full name lives in
        // the tooltip.
        formatter: (name: string) => (name.length > 18 ? name.slice(0, 17) + "…" : name),
      },
      splitArea: { areaStyle: { color: ["rgba(17,24,39,0.02)", "rgba(17,24,39,0.04)"] } },
      splitLine: { lineStyle: { color: "rgba(17,24,39,0.08)" } },
      axisLine: { lineStyle: { color: "rgba(17,24,39,0.1)" } },
    },
    series: [
      {
        type: "radar",
        data: seriesData,
      },
    ],
  } as ChartOption;
}
